import { useQuery } from '@powersync/react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  type TemplateMemberProfile,
  type TemplateRosterCollaborator,
  type TemplateRosterOwner,
} from '@/lib/data';
import { hapticClaimBlocked } from '@/lib/haptics';
import { rosterColorSlots } from '@/lib/presence/claims';
import { PALETTE_SIZE } from '@/lib/presence/presenceColors';
import { createSupabaseBroadcastChannel } from '@/lib/presence/supabaseChannel';
import { useClaimGate } from '@/lib/presence/useClaimGate';
import { usePresencePalette } from '@/lib/presence/usePresencePalette';
import { usePresenceSession } from '@/lib/presence/usePresenceSession';

import { claimantsEditingLabel } from './claimantsLabel';
import { type FieldClaimDisplay } from './fieldClaimDisplay';
import { PresenceToast, type PresenceToastContent } from './PresenceToast';
import {
  TemplateFieldPresenceContext,
  type EditBlockedTarget,
  type TemplateFieldPresenceValue,
} from './useTemplateFieldPresence';

const TOAST_MS = 6000;

/**
 * Field presence for one template's editor: joins the template's presence
 * channel, publishes the local user's focus claims (editors only — spectators
 * subscribe without announcing), and provides claim lookups, edit gating, and
 * the blocked-tap toast to every claimable field below it.
 */
export function TemplateFieldPresence({
  templateId,
  userId,
  canEdit,
  children,
}: {
  templateId: string;
  userId: string | null;
  canEdit: boolean;
  children: ReactNode;
}) {
  const session = usePresenceSession({
    topic: `template:${templateId}`,
    userId,
    canClaim: canEdit,
    createChannel: createSupabaseBroadcastChannel,
  });
  const gate = useClaimGate(session.claims);
  const palette = usePresencePalette();

  // The per-template projection of member display names — the only peer-name
  // source synced to every member (global profiles are self-only).
  const { data: profiles } = useQuery<TemplateMemberProfile>(
    'SELECT template_id, user_id, display_name FROM template_member_profiles WHERE template_id = ?',
    [templateId],
  );
  const names = useMemo(() => {
    const map = new Map<string, string>();
    // A null display_name stays out of the map; claimsFor's lookup fallback
    // covers it together with claimants missing from the projection entirely.
    for (const p of profiles)
      if (p.display_name != null) map.set(p.user_id, p.display_name);
    return map;
  }, [profiles]);

  // Presence colors derive from the synced membership — a pure function every
  // client computes identically, so no color is ever announced or negotiated
  // (ADR 0002; CONTEXT.md: Presence color). The owner has no
  // template_collaborators row (only share-link redeemers do), so the
  // templates row supplies them; created_at predates every redemption's
  // joined_at, putting the owner first.
  const { data: roster } = useQuery<TemplateRosterCollaborator>(
    'SELECT user_id, joined_at FROM template_collaborators WHERE template_id = ?',
    [templateId],
  );
  const { data: ownerRows } = useQuery<TemplateRosterOwner>(
    'SELECT owner_id, created_at FROM templates WHERE id = ?',
    [templateId],
  );
  const colorSlots = useMemo(() => {
    const members = roster.map((r) => ({
      userId: r.user_id,
      joinedAt: r.joined_at ?? '',
    }));
    const owner = ownerRows[0];
    if (owner?.owner_id && !members.some((m) => m.userId === owner.owner_id)) {
      members.push({ userId: owner.owner_id, joinedAt: owner.created_at ?? '' });
    }
    return rosterColorSlots(members, PALETTE_SIZE);
  }, [roster, ownerRows]);

  const claimsFor = useCallback(
    (fieldKey: string): FieldClaimDisplay[] => {
      const claims = session.claims[fieldKey] ?? [];
      return claims.map((claim) => ({
        userId: claim.userId,
        name: names.get(claim.userId) ?? 'Unknown user',
        // A claimant absent from the roster snapshot (just-removed member)
        // falls back to slot 0; the name label still identifies them.
        color: palette[colorSlots.get(claim.userId) ?? 0]!,
      }));
    },
    [session.claims, names, colorSlots, palette],
  );

  // Focus deferred to an effect: it must run after the re-render that flips
  // the target's editable/readOnly state (a blocked native input can't focus),
  // and after an add-affordance's freshly mounted input attaches its ref.
  // Each override sets a fresh object, so the effect fires exactly once per
  // override — no clearing needed.
  const [pendingFocus, setPendingFocus] = useState<{ focus: () => void } | null>(null);
  useEffect(() => {
    pendingFocus?.focus();
  }, [pendingFocus]);

  const [toast, setToast] = useState<{
    content: PresenceToastContent;
    action: () => void;
  } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const showToast = useCallback(
    (message: string, actionLabel: string, action: () => void) => {
      hapticClaimBlocked();
      setToast({ content: { message, actionLabel }, action });
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
    },
    [],
  );

  // Native can deliver the next field's focus before the previous field's
  // blur, so a blur only clears the published claim while its field is still
  // the focused one — a stale blur must not wipe a just-announced claim.
  const focusedFieldRef = useRef<string | null>(null);

  const fieldFocused = useCallback(
    (fieldKey: string) => {
      focusedFieldRef.current = fieldKey;
      gate.fieldFocused(fieldKey);
      session.setFocusedField(fieldKey);
    },
    [gate, session],
  );

  const fieldBlurred = useCallback(
    (fieldKey: string) => {
      gate.fieldBlurred(fieldKey);
      if (focusedFieldRef.current === fieldKey) {
        focusedFieldRef.current = null;
        session.setFocusedField(null);
      }
    },
    [gate, session],
  );

  const editBlockedPress = useCallback(
    (fieldKey: string, target?: EditBlockedTarget) => {
      const claims = claimsFor(fieldKey);
      if (claims.length === 0) return;
      showToast(claimantsEditingLabel(claims, 'field'), 'Edit anyway', () => {
        gate.override(fieldKey);
        target?.open?.();
        if (target) setPendingFocus({ focus: target.focus });
      });
    },
    [claimsFor, showToast, gate],
  );

  const deleteBlockedPress = useCallback(
    (fieldKeys: string[], removeAnyway: () => void) => {
      // Per-field claims already count a multi-device user once, but the same
      // user can hold different fields of the step from different devices —
      // dedupe across fields so they aren't named twice.
      const seen = new Set<string>();
      const claims = fieldKeys
        .flatMap((key) => claimsFor(key))
        .filter((claim) => {
          if (seen.has(claim.userId)) return false;
          seen.add(claim.userId);
          return true;
        });
      if (claims.length === 0) return;
      showToast(claimantsEditingLabel(claims, 'step'), 'Delete anyway', removeAnyway);
    },
    [claimsFor, showToast],
  );

  const value = useMemo<TemplateFieldPresenceValue>(
    () => ({
      claimsFor,
      isBlocked: gate.isBlocked,
      fieldFocused,
      fieldBlurred,
      editBlockedPress,
      deleteBlockedPress,
    }),
    [
      claimsFor,
      gate.isBlocked,
      fieldFocused,
      fieldBlurred,
      editBlockedPress,
      deleteBlockedPress,
    ],
  );

  return (
    <TemplateFieldPresenceContext.Provider value={value}>
      {children}
      <PresenceToast
        toast={toast?.content ?? null}
        onAction={() => {
          const action = toast?.action;
          setToast(null);
          action?.();
        }}
        onDismiss={() => setToast(null)}
      />
    </TemplateFieldPresenceContext.Provider>
  );
}
